/**
 * Phase 1.7 — recent activity widget.
 *
 * Replaces the v1 monolith's "Recent Activity" section. Reads from
 * DashboardSummary.recentActivity.events. Empty state uses the v2
 * EmptyState (card variant) so the chart row stays the same height.
 */

import { ScrollText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Card, EmptyState } from '@/components/v2';
import type { LogEntry } from '@shared/types';

export interface RecentActivityCardProps {
  events: LogEntry[];
  limit?: number;
}

const TONE: Record<string, string> = {
  maintenance: 'text-sky-muted bg-sky-muted/10 dark:text-sky-muted-dark dark:bg-sky-muted-dark/15',
  pasture: 'text-forest bg-forest/10 dark:text-forest-dark dark:bg-forest-dark/15',
  vet: 'text-moss bg-moss/10 dark:text-moss-dark dark:bg-moss-dark/15',
  'animal-care': 'text-burnt-orange bg-burnt-orange/10 dark:text-burnt-orange-dark dark:bg-burnt-orange-dark/15',
};

export function RecentActivityCard({ events, limit = 6 }: RecentActivityCardProps): JSX.Element {
  const visible = events.slice(0, limit);
  return (
    <Card title="Recent activity" subtitle={`${events.length} events`}>
      {visible.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ScrollText}
          title="Nothing logged yet"
          description="Use Quick Log to capture work as it happens."
        />
      ) : (
        <ul className="divide-y divide-border">
          {visible.map((e) => (
            <li key={e.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <span
                className={`mt-0.5 inline-flex shrink-0 rounded px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${
                  TONE[e.log_type] ?? 'text-muted-foreground bg-surface-raised dark:bg-surface-raised-dark'
                }`}
              >
                {e.log_type}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-charcoal dark:text-charcoal-dark">{e.description}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {format(parseISO(e.date ?? e.created_at), 'MMM d, h:mm a')}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
